import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker, DropdownProps } from 'react-day-picker';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/design-system/Select';

import { cn } from '@/utils/styles';
import { buttonVariants } from '@/components/design-system/Button';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

export function CustomSelectDropdown(props: DropdownProps) {
  const { children, value, onChange } = props;
  const handleValueChange = (newValue: string) => {
    if (onChange) {
      const syntheticEvent = {
        target: {
          value: newValue,
        },
      } as React.ChangeEvent<HTMLSelectElement>;

      onChange(syntheticEvent);
    }
  };

  return (
    <Select value={value?.toString()} onValueChange={handleValueChange}>
      <SelectTrigger className="w-[100px] sm:w-[125px]">
        <SelectValue placeholder="Select" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child)) {
              const childProps = child.props;
              return (
                <SelectItem
                  key={childProps.value}
                  value={childProps.value.toString()}
                  disabled={childProps.disabled}
                >
                  {childProps.children}
                </SelectItem>
              );
            }
            return null;
          })}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <div className="flex items-center flex-col gap-6 sm:gap-16">
      <DayPicker
        showOutsideDays={showOutsideDays}
        fixedWeeks
        className={cn('p-0 sm:p-4 rounded-sm', className)}
        classNames={{
          months:
            'w-full flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0 bg-[white] rounded-sm p-3 sm:p-6',
          month: 'space-y-4',
          caption: 'flex justify-center pt-4 relative items-center',
          caption_dropdowns: 'flex flex-row gap-[10px]',
          caption_label: 'typography-h2 pb-2 pt-2 hidden',
          nav: 'space-x-1 flex items-center',
          nav_button: 'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
          nav_button_previous: 'absolute left-1',
          nav_button_next: 'absolute right-1',
          table: 'w-full border-collapse',
          head_cell: 'text-muted-foreground font-normal text-sm',
          cell: cn(
            'relative p-1 sm:p-3 text-center text-sm focus-within:relative focus-within:z-20 rounded-r-md',
            props.mode === 'range'
              ? '[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md'
              : '[&:has([aria-selected])]:rounded-md',
          ),
          day: cn(
            buttonVariants({ variant: 'ghost' }),
            'h-8 w-8 p-0 font-normal aria-selected:opacity-100',
          ),
          day_range_start: 'day-range-start',
          day_range_end: 'day-range-end',
          day_selected:
            'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
          day_today: 'bg-accent text-accent-foreground',
          day_outside:
            'day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground',
          day_disabled: 'text-muted-foreground opacity-50',
          day_range_middle:
            'aria-selected:bg-accent aria-selected:text-accent-foreground',
          day_hidden: 'invisible',
          ...classNames,
        }}
        components={{
          IconLeft: ({ className, ...props }) => (
            <ChevronLeft className={cn('h-6 w-6', className)} {...props} />
          ),
          IconRight: ({ className, ...props }) => (
            <ChevronRight className={cn('h-6 w-6', className)} {...props} />
          ),
          Dropdown: CustomSelectDropdown,
        }}
        {...props}
      />
    </div>
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
